import { compact, isArray } from 'lodash-es';
import { FieldsArray } from '../pages/measuringPoint/fields-array';
import type { TranslatedFormField, TranslatedFormLayout } from '../pages/measuringPoint/types';

type LayoutNode = readonly [string, string | null, string | readonly LayoutNode[]];

type FlatLayoutItem = [fieldName: string, groupName: string, groupTitle: string];

interface LayoutTreeProps {
    name: string;
    title?: string;
    fields?: TranslatedFormField[];
    groups?: LayoutTree[];
}

export class LayoutTree {
    public name: string;

    public title: string;

    public fields: FieldsArray<TranslatedFormField>;

    public groups: FieldsArray<LayoutTree>;

    private cachedFieldNames?: string[];

    constructor({ name, title = '', fields = [], groups = [] }: LayoutTreeProps) {
        this.name = name;
        this.title = title;
        this.fields = new FieldsArray(...fields);
        this.groups = new FieldsArray(...groups);
    }

    getFieldNames(): string[] {
        if (this.cachedFieldNames) {
            return this.cachedFieldNames;
        }

        const directNames = Array.from(this.fields, (field) => field.name);
        const nestedNames = this.groups.flatMap((group) => group.getFieldNames());
        this.cachedFieldNames = Array.from(new Set([...directNames, ...nestedNames]));

        return this.cachedFieldNames;
    }
}

export class LayoutHelper {
    constructor(
        public readonly layout: readonly TranslatedFormLayout[],
        public readonly fields: readonly TranslatedFormField[]
    ) {}

    getFieldsStartingAt(name: string): Record<string, TranslatedFormField> {
        function mapItems([groupName, groupTitle, groupContent]: LayoutNode): FlatLayoutItem[] {
            if (isArray(groupContent)) {
                return groupContent.flatMap(mapItems);
            }
            // @ts-expect-error - Ignoring because groupContent can be an array.
            return [[groupContent, groupName, groupTitle || '']];
        }

        const objectLayout = Object.fromEntries(
            (this.layout as readonly LayoutNode[])
                .flatMap(mapItems)
                .map(([groupContent, groupName, groupTitle]) => [
                    groupContent,
                    [groupName, groupTitle],
                ])
        );

        let state = 0;

        // state 0: We have not found the field entry of this group yet.
        // state 1: We are outputting all entry's we find.
        // state 2: We found a field belonging to another group, stop outputting.
        return Object.fromEntries(
            compact(
                this.fields.map((field) => {
                    if (state === 1 && objectLayout[field.name] && field.name !== name) {
                        state = 2;
                    }
                    if (state === 2 || (state === 0 && field.name !== name)) {
                        return null;
                    }
                    if (state === 0) {
                        state = 1;
                    }
                    return [field.name, field];
                })
            )
        );
    }

    convertGroup = ([name, title, content]: LayoutNode): LayoutTree =>
        new LayoutTree({
            name,
            title: title || undefined,
            ...(isArray(content)
                ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  { groups: content.map((group) => this.convertGroup(group)) }
                : // @ts-expect-error - Ignoring because content can be an array.
                  { fields: Object.values(this.getFieldsStartingAt(content)) }),
        });

    createTree(): LayoutTree {
        const rootNode: LayoutNode = ['root', '', this.layout];
        return this.convertGroup(rootNode);
    }
}
